// =====================================================================================
// INVRT Radar — web console — account — delivery — one target's attempt history
//
// GET /delivery/targets/:id/attempts (contract §6), newest first, in a modal "drawer" on
// the house Dialog. Each row is one delivery_attempts record: the event, the contact it
// carried (by its `vis_`/`rev_` id, never the person's details), its state, how many
// tries, the last HTTP status and the destination's last error. "Retry" (admin only)
// re-queues THAT attempt — POST /delivery/attempts/:id/retry — and the list is re-read.
// =====================================================================================

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/format";
import type { DeliveryAttempt, DeliveryTarget } from "@/lib/types.c1";
import { DeliveryStateBadge } from "./badges";
import {
  attemptsKey,
  DRAWER_PAGE_LIMIT,
  eventKindLabel,
  httpStatusText,
  TARGET_KIND_LABELS,
  useDeliveryAttempts,
} from "./hooks";

export interface AttemptsDrawerProps {
  target: DeliveryTarget;
  isAdmin: boolean;
  onClose: () => void;
}

export function AttemptsDrawer({ target, isAdmin, onClose }: AttemptsDrawerProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const attempts = useDeliveryAttempts(target.id);

  const retry = useMutation({
    mutationFn: (attempt: DeliveryAttempt) =>
      api.post<DeliveryAttempt>(`/delivery/attempts/${attempt.id}/retry`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: attemptsKey(orgId, target.id) });
      push({
        tone: "success",
        title: "Retry queued",
        body: `The contact is sent to ${target.label} again shortly.`,
      });
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Delivery attempts"
      description={`${target.label} (${TARGET_KIND_LABELS[target.kind]})`}
      className="w-[min(64rem,calc(100vw-2rem))]"
    >
      {attempts.isLoading ? <LoadingState label="Loading attempts…" /> : null}
      {attempts.isError ? (
        <ErrorState error={attempts.error} onRetry={() => void attempts.refetch()} />
      ) : null}
      {attempts.isSuccess ? (
        attempts.data.attempts.length === 0 ? (
          <EmptyState
            title="No attempts yet"
            body="Every contact Radar sends to this target is listed here with what the destination answered."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {retry.isError ? <ErrorState error={retry.error} /> : null}
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Event</TableHeaderCell>
                  <TableHeaderCell>Contact</TableHeaderCell>
                  <TableHeaderCell>State</TableHeaderCell>
                  <TableHeaderCell className="text-right">Tries</TableHeaderCell>
                  <TableHeaderCell>Last status</TableHeaderCell>
                  <TableHeaderCell>Last error</TableHeaderCell>
                  <TableHeaderCell>Last attempt</TableHeaderCell>
                  {isAdmin ? <TableHeaderCell className="text-right">Actions</TableHeaderCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {attempts.data.attempts.map((attempt) => (
                  <TableRow key={attempt.id}>
                    <TableCell>{eventKindLabel(attempt.event_kind)}</TableCell>
                    <TableCell className="font-mono text-xs text-ink-muted">
                      {attempt.contact_id}
                    </TableCell>
                    <TableCell>
                      <DeliveryStateBadge state={attempt.state} />
                    </TableCell>
                    <TableCell className="text-right font-mono">{attempt.attempts}</TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {httpStatusText(attempt.last_status_code)}
                    </TableCell>
                    <TableCell className="max-w-xs text-ink-muted">
                      {attempt.last_error ?? "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(attempt.last_attempt_at)}
                    </TableCell>
                    {isAdmin ? (
                      <TableCell className="text-right">
                        {attempt.state === "dead" || attempt.state === "failed" ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => retry.mutate(attempt)}
                            loading={retry.isPending && retry.variables?.id === attempt.id}
                          >
                            Retry
                          </Button>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {attempts.data.next_cursor ? (
              <p className="text-xs text-ink-muted">
                Showing the most recent {DRAWER_PAGE_LIMIT} attempts.
              </p>
            ) : null}
          </div>
        )
      ) : null}
    </Dialog>
  );
}
